import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { Infinity as InfinityIcon } from "lucide-react";
import type { WorkspaceId } from "@/packages/types";
import { useTranslation } from "@/packages/utils";
import { useIsByok } from "@/hooks/use-is-byok";
import { CreditExhaustedPrompt } from "@/components/ai/CreditExhaustedPrompt";
import {
  SettingsPageHeader,
  SettingsPanel,
  SettingsSection,
} from "./SettingsSection";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type BalanceData = {
  workspaceId: WorkspaceId;
  monthlyAllocation: number;
  creditsUsed: number;
  creditsRemaining: number;
  periodStart: number;
  periodEnd: number;
  overageAllowed: boolean;
  usagePercent: number;
};

type OperationData = {
  operation: string;
  totalCredits: number;
  count: number;
  totalTokensInput: number;
  totalTokensOutput: number;
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function getUsageColor(percent: number): string {
  if (percent > 90) return "bg-red-500";
  if (percent > 75) return "bg-orange-500";
  if (percent > 50) return "bg-yellow-500";
  return "bg-emerald-500";
}

function getUsageTextColor(percent: number): string {
  if (percent > 90) return "text-red-500";
  if (percent > 75) return "text-orange-500";
  if (percent > 50) return "text-yellow-500";
  return "text-emerald-500";
}

function formatPeriodDates(
  periodStart: number,
  periodEnd: number,
  locale: string,
) {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);

  const monthYear = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(start);

  const resetDate = new Intl.DateTimeFormat(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(end);

  return { monthYear, resetDate };
}

function formatTokens(n: number): string {
  if (n === 0) return "\u2014";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function humanizeOperationName(operation: string): string {
  return operation.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function createZeroBalance(): BalanceData {
  const now = new Date();
  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const periodEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  ).getTime();

  return {
    workspaceId: "" as WorkspaceId,
    monthlyAllocation: 0,
    creditsUsed: 0,
    creditsRemaining: 0,
    periodStart,
    periodEnd,
    overageAllowed: false,
    usagePercent: 0,
  };
}

/* ------------------------------------------------------------------ */
/* CreditUsageBar                                                      */
/* ------------------------------------------------------------------ */

function CreditUsageBar({ balance }: { balance: BalanceData }) {
  const { t, i18n } = useTranslation("settings");
  const { monthYear, resetDate } = formatPeriodDates(
    balance.periodStart,
    balance.periodEnd,
    i18n.resolvedLanguage ?? i18n.language ?? "de",
  );
  const barColor = getUsageColor(balance.usagePercent);
  const textColor = getUsageTextColor(balance.usagePercent);
  const clampedPercent = Math.min(balance.usagePercent, 100);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted">
          {monthYear} &middot; {t("aiUsage.resets", { date: resetDate })}
        </p>
        <span className={`text-2xl font-bold ${textColor}`}>
          {balance.usagePercent}%
        </span>
      </div>

      <div className="space-y-2">
        <div className="h-3 w-full rounded-full bg-border/50 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${clampedPercent}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-secondary">
          <span>
            <span className="font-medium text-primary">
              {balance.creditsUsed.toLocaleString()}
            </span>{" "}
            {t("aiUsage.creditsUsedOf", {
              total: balance.monthlyAllocation.toLocaleString(),
            })}
          </span>
          <span>
            {t("aiUsage.creditsRemaining", {
              count: balance.creditsRemaining,
            })}
          </span>
        </div>
      </div>

      {balance.overageAllowed && balance.usagePercent > 90 && (
        <p className="text-xs text-orange-500">{t("aiUsage.overageNotice")}</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* UsageBreakdownTable                                                 */
/* ------------------------------------------------------------------ */

function UsageBreakdownTable({ data }: { data: OperationData[] }) {
  const { t } = useTranslation("settings");

  if (data.length === 0) {
    return (
      <p className="text-xs text-muted py-6 text-center">
        {t("aiUsage.empty")}
      </p>
    );
  }

  const totalCredits = data.reduce((sum, d) => sum + d.totalCredits, 0);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-border">
              <th className="pb-2 text-xs font-medium text-muted">
                {t("aiUsage.tableHeaders.operation")}
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                {t("aiUsage.tableHeaders.creditsUsed")}
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                {t("aiUsage.tableHeaders.requests")}
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                {t("aiUsage.tableHeaders.tokensIn")}
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                {t("aiUsage.tableHeaders.tokensOut")}
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                {t("aiUsage.tableHeaders.avgCreditsPerRequest")}
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                {t("aiUsage.tableHeaders.percentOfTotal")}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const avg =
                row.count > 0
                  ? Math.round((row.totalCredits / row.count) * 10) / 10
                  : 0;
              const pctOfTotal =
                totalCredits > 0
                  ? Math.round((row.totalCredits / totalCredits) * 100)
                  : 0;
              const operationLabel = t(`aiUsage.operations.${row.operation}`, {
                defaultValue: humanizeOperationName(row.operation),
              });

              return (
                <tr
                  key={row.operation}
                  className="border-b border-border/50 last:border-0"
                >
                  <td className="py-2.5 text-[13px] text-primary font-medium">
                    {operationLabel}
                  </td>
                  <td className="py-2.5 text-[13px] text-secondary text-right tabular-nums">
                    {row.totalCredits.toLocaleString()}
                  </td>
                  <td className="py-2.5 text-[13px] text-secondary text-right tabular-nums">
                    {row.count.toLocaleString()}
                  </td>
                  <td className="py-2.5 text-[13px] text-secondary text-right tabular-nums">
                    {formatTokens(row.totalTokensInput)}
                  </td>
                  <td className="py-2.5 text-[13px] text-secondary text-right tabular-nums">
                    {formatTokens(row.totalTokensOutput)}
                  </td>
                  <td className="py-2.5 text-[13px] text-secondary text-right tabular-nums">
                    {avg}
                  </td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-border/50 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent"
                          style={{ width: `${pctOfTotal}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted w-8 text-right tabular-nums">
                        {pctOfTotal}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Loading skeleton                                                    */
/* ------------------------------------------------------------------ */

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="flex justify-between">
        <div className="space-y-2">
          <div className="h-4 w-32 bg-muted/30 rounded" />
          <div className="h-3 w-48 bg-muted/20 rounded" />
        </div>
        <div className="h-8 w-12 bg-muted/30 rounded" />
      </div>
      <div className="h-3 w-full bg-muted/20 rounded-full" />
      <div className="flex justify-between">
        <div className="h-3 w-36 bg-muted/20 rounded" />
        <div className="h-3 w-24 bg-muted/20 rounded" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */

export function AiUsageSection() {
  const { t } = useTranslation("settings");
  const isByok = useIsByok();

  const balance = useQuery(
    api.ai_credit_queries.getMyBalance,
    isByok ? "skip" : {},
  );
  const usageByOperation = useQuery(
    api.ai_credit_queries.getUsageByOperation,
    isByok ? "skip" : {},
  );

  // Loading state — wait for BYOK check and (when not BYOK) credit queries
  if (
    isByok === undefined ||
    (!isByok && (balance === undefined || usageByOperation === undefined))
  ) {
    return (
      <SettingsSection
        title={t("aiUsage.title") as string}
        description={t("aiUsage.description") as string}
      >
        <LoadingSkeleton />
      </SettingsSection>
    );
  }

  // BYOK — replace usage display with Unlimited section
  if (isByok) {
    return (
      <SettingsSection
        title={t("aiUsage.title") as string}
        description={t("aiUsage.description") as string}
      >
        <div className="flex items-center gap-3">
          <InfinityIcon size={24} className="text-accent shrink-0" />
          <div>
            <p className="text-sm font-semibold text-primary">
              {t("aiUsage.byok.unlimited")}
            </p>
            <p className="text-xs text-muted">
              {t("aiUsage.byok.description")}
            </p>
          </div>
        </div>
      </SettingsSection>
    );
  }

  const resolvedBalance = balance ?? createZeroBalance();

  const isExhausted =
    resolvedBalance.monthlyAllocation > 0 &&
    resolvedBalance.creditsRemaining <= 0;

  return (
    <div className="flex flex-col gap-8">
      <SettingsPageHeader
        eyebrow={t("sections.organization") as string}
        title={t("aiUsage.title") as string}
        description={t("aiUsage.description") as string}
      />
      <SettingsPanel>
        {isExhausted && (
          <div className="mb-4">
            <CreditExhaustedPrompt
              monthlyAllocation={resolvedBalance.monthlyAllocation}
            />
          </div>
        )}
        <CreditUsageBar balance={resolvedBalance} />
      </SettingsPanel>

      <SettingsSection title={t("aiUsage.byOperationTitle") as string}>
        <UsageBreakdownTable data={usageByOperation ?? []} />
      </SettingsSection>
    </div>
  );
}
